import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';
import { useFollowList } from './useFollowList';

/**
 * Follow/unfollow actions on the user's kind 3 contact list (NIP-02).
 *
 * Both actions fetch the user's *freshest* kind 3 event from relays first
 * (not the React Query cache) and preserve its content and all existing tags,
 * so no petnames or relay metadata are lost.
 */
export function useFollowActions(targetPubkey: string | undefined) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { pubkeys: followedPubkeys, isLoading: followListLoading } = useFollowList(user?.pubkey);
  const isFollowing = !!targetPubkey && followedPubkeys.includes(targetPubkey);

  const mutation = useMutation({
    mutationFn: async (action: 'follow' | 'unfollow') => {
      if (!user) throw new Error('You must be logged in');
      if (!targetPubkey) throw new Error('No target pubkey');
      if (targetPubkey === user.pubkey) throw new Error('You cannot follow yourself');

      // Fetch the freshest kind 3 directly from relays — the cache may be stale
      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.timeout(8000) },
      );

      const latest = events.sort((a, b) => b.created_at - a.created_at)[0];
      const content = latest?.content ?? '';
      const tags = latest ? latest.tags.filter(t => Array.isArray(t) && t.length >= 2) : [];

      const alreadyFollowing = tags.some(t => t[0] === 'p' && t[1] === targetPubkey);

      if (action === 'follow') {
        if (alreadyFollowing) return action;
        tags.push(['p', targetPubkey]);
      } else {
        if (!alreadyFollowing) return action;
        // Remove ALL p tags pointing at the target
        for (let i = tags.length - 1; i >= 0; i--) {
          if (tags[i][0] === 'p' && tags[i][1] === targetPubkey) {
            tags.splice(i, 1);
          }
        }
      }

      await createEvent({ kind: 3, content, tags });
      return action;
    },
    onSuccess: (action) => {
      // Refresh follow lists everywhere
      queryClient.invalidateQueries({ queryKey: ['followList'] });
      toast({
        title: action === 'follow' ? 'Subscribed' : 'Unsubscribed',
        description:
          action === 'follow'
            ? 'Their videos will appear in your Subscribed feed.'
            : 'Their videos will no longer appear in your Subscribed feed.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update follow list',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  return {
    isFollowing,
    isLoading: followListLoading,
    follow: () => mutation.mutate('follow'),
    unfollow: () => mutation.mutate('unfollow'),
    isPending: mutation.isPending,
  };
}
